import React from "react";
import { ArrowUp, Target, TrendingUp, Dumbbell } from "lucide-react";

const DifficultyAnalysisSection = ({ difficultyData }) => {
  const getConfig = (difficulty) => {
    const configs = {
      easy: {
        color: "text-emerald-600",
        bg: "bg-emerald-50",
        border: "border-emerald-200",
        bar: "bg-emerald-500",
        icon: TrendingUp,
      },
      medium: {
        color: "text-amber-600",
        bg: "bg-amber-50",
        border: "border-amber-200",
        bar: "bg-amber-500",
        icon: Dumbbell,
      },
      hard: {
        color: "text-red-600",
        bg: "bg-red-50",
        border: "border-red-200",
        bar: "bg-red-500",
        icon: Target,
      },
    };

    return configs[difficulty] || configs.medium;
  };

  const summaryCards = [
    {
      title: "Strongest",
      icon: ArrowUp,
      data: difficultyData.strongest,
    },
    {
      title: "Focus Area",
      icon: Target,
      data: difficultyData.weakest,
    },
  ];

  return (
    <div className="bg-white rounded-xl border border-gray-200 p-4">
      {/* Header */}
      <div className="mb-4">
        <h2 className="font-semibold text-gray-900">Difficulty Analysis</h2>
      </div>

      {/* Strongest & Weakest */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-4">
        {summaryCards.map((card) => {
          const config = getConfig(card.data?.difficulty);

          return (
            <div
              key={card.title}
              className={`${config.bg} ${config.border} border rounded-lg p-3`}
            >
              <div className="flex items-center justify-between mb-2">
                <div className="flex items-center gap-2">
                  <card.icon className={`w-4 h-4 ${config.color}`} />
                  <span className="text-sm font-medium text-gray-700">
                    {card.title}
                  </span>
                </div>

                <span className={`text-sm font-bold ${config.color}`}>
                  {card.data?.accuracy}%
                </span>
              </div>

              <div className="flex items-center justify-between text-sm">
                <span className="capitalize font-semibold text-gray-900">
                  {card.data?.difficulty}
                </span>

                <span className="text-gray-500">
                  {card.data?.totalQuestions} Qs
                </span>
              </div>

              <div className="mt-2 h-1.5 bg-white rounded-full overflow-hidden">
                <div
                  className={`${config.bar} h-full rounded-full`}
                  style={{
                    width: `${card.data?.accuracy}%`,
                  }}
                />
              </div>
            </div>
          );
        })}
      </div>

      {/* All Levels */}
      <div className="border border-gray-100 rounded-lg p-3 mb-4">
        <div className="space-y-3">
          {difficultyData.difficulties.map((level, index) => {
            const config = getConfig(level.difficulty);
            const Icon = config.icon;

            return (
              <div key={index}>
                <div className="flex justify-between items-center mb-1">
                  <div className="flex items-center gap-2">
                    <Icon className={`w-3.5 h-3.5 ${config.color}`} />
                    <span className="text-sm capitalize text-gray-700">
                      {level.difficulty}
                    </span>
                    <span className="text-xs text-gray-500">
                      ({level.totalQuestions})
                    </span>
                  </div>

                  <span className={`text-xs font-semibold ${config.color}`}>
                    {level.accuracy}%
                  </span>
                </div>

                <div className="h-1.5 bg-gray-100 rounded-full overflow-hidden">
                  <div
                    className={`${config.bar} h-full rounded-full`}
                    style={{
                      width: `${level.accuracy}%`,
                    }}
                  />
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Insight */}
      <div className="px-3 py-2 bg-amber-50 border border-amber-200 rounded-lg">
        <div className="text-sm font-medium text-amber-900">
          {difficultyData.title}
        </div>

        <p className="text-xs text-amber-700 mt-1">
          {difficultyData.description}
        </p>
      </div>
    </div>
  );
};

export default DifficultyAnalysisSection;
